import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import classnames from 'classnames';
import lodashValues from 'lodash/fp/values';
import lodashGet from 'lodash/fp/get';

import { USER_ROLE_ADMIN } from 'common/userRoles';
import H1 from 'client/components/H1';
import SrLogo from 'client/components/SrLogo';
import SrDataTableAdminTours from 'client/components/SrDataTableAdminTours';
import SrSearchByPhoneHouseholdIdAndRedirectForm from 'client/components/SrSearchByPhoneAndRedirectForm';
import SrAdminNavbarLinks from 'client/components/SrAdminNavbarLinks';
import SrUserSettingDropdown from 'client/components/SrUserSettingDropdown';
import SrUserSettingListingManagementCompanyDropdown from 'client/components/SrUserSettingListingManagementCompanyDropdown';

import {
  DATA_TABLE_SOURCE_ADMIN_TOURS,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED_INDICATOR,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_ALL,
} from 'common/dataTableConstants';

import {
  SCREEN_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
  SCREEN_ID_ADMIN_TOURS_REQUESTED,
  SCREEN_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
  SCREEN_ID_ADMIN_TOURS_SCHEDULED,
  SCREEN_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
  SCREEN_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED,
  SCREEN_ID_ADMIN_TOURS_COMPLETED,
  SCREEN_ID_ADMIN_TOURS_ALL,
} from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import branch from 'client/core/branch';
import AgentActions from 'client/actions/Agent';
import HouseholdActions from 'client/actions/Household';
import AdminActions from 'client/actions/Admin';
import MarketActions from 'client/actions/Market';
import PartnerActions from 'client/actions/Partner';

const toursAdminLinks = [
  {
    id: 'needs-rescheduling',
    label: 'needs rescheduling',
    value: '/admin/tours/needs-rescheduling',
  },
  {
    id: 'requested',
    label: 'requested',
    value: '/admin/tours/requested/all',
    indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
    indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED],
  },
  {
    id: 'scheduled',
    label: 'scheduled',
    value: '/admin/tours/scheduled/all',
    indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
    indicatorViewIds: [
      DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
      DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED_INDICATOR,
    ],
  },
  {
    id: 'completed',
    label: 'completed',
    value: '/admin/tours/completed',
    indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
    indicatorViewIds: [
      DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED,
      DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED,
    ],
  },
];

const ADMIN_TOURS_PAGE_PATH_TO_PAGE_PARAMS_MAPPING = {
  'needs-rescheduling': {
    headline: 'needs rescheduling',
    screenId: SCREEN_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
    showSort: true,
    showFilters: true,
    sublinks: [],
  },
  requested: {
    headline: 'requested tours',
    screenId: SCREEN_ID_ADMIN_TOURS_REQUESTED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED,
    showSort: true,
    showFilters: true,
    sublinks: [
      {
        id: 'all',
        label: 'all',
        value: '/admin/tours/requested/all',
        indicatorViewIds: null,
      },
      {
        id: 'recently-added',
        label: 'recently added',
        value: '/admin/tours/requested/recently-added',
        indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED],
      },
    ],
  },
  scheduled: {
    headline: 'scheduled tours',
    screenId: SCREEN_ID_ADMIN_TOURS_SCHEDULED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED,
    showSort: true,
    showFilters: true,
    sublinks: [
      {
        id: 'all',
        label: 'all',
        value: '/admin/tours/scheduled/all',
      },
      {
        id: 'added-to-tour',
        label: 'added to tour',
        value: '/admin/tours/scheduled/added-to-tour',
        indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR],
      },
      {
        id: 'unassigned',
        label: 'unassigned',
        value: '/admin/tours/scheduled/unassigned',
        indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED_INDICATOR],
      },
    ],
  },
  completed: {
    headline: 'completed tours',
    screenId: SCREEN_ID_ADMIN_TOURS_COMPLETED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED,
    showSort: false,
    showFilters: true,
    sublinks: [
      {
        id: 'all',
        label: 'all',
        value: '/admin/tours/completed/all',
      },
      {
        id: 'unasked',
        label: 'un-asked',
        value: '/admin/tours/completed/un-asked',
        indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED],
      },
      {
        id: 'unanswered',
        label: 'un-answered',
        value: '/admin/tours/completed/un-answered',
        indicatorDataTableSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        indicatorViewIds: [DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED],
      },
    ],
  },
  all: {
    headline: 'all tours',
    screenId: SCREEN_ID_ADMIN_TOURS_ALL,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_ALL,
    showSort: false,
    showFilters: false,
    sublinks: [],
  },
};

const ADMIN_TOURS_PAGE_PATH_TO_PAGE_SUB_PARAMS_MAPPING = {
  'recently-added': {
    screenId: SCREEN_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
  },
  'added-to-tour': {
    screenId: SCREEN_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
  },
  unassigned: {
    screenId: SCREEN_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED,
  },
  'un-asked': {
    screenId: SCREEN_ID_ADMIN_TOURS_COMPLETED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED,
  },
  'un-answered': {
    screenId: SCREEN_ID_ADMIN_TOURS_COMPLETED,
    dataTableViewId: DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED,
  },
};

const TOUR_ADMIN_SORT_BY_START_DATE = 'startDate';
const TOUR_ADMIN_SORT_BY_RECENT_ACTIVITY = 'lastStatusUpdateDate';

const USER_SETTING_TOUR_SUPPORT_FIELD_MARKET_ID_ALL =
  'USER_SETTING_TOUR_SUPPORT_FIELD_MARKET_ID_ALL';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(({ match: { params: { tourStateSubPath, tourStatePath } } }) =>
  tourStateSubPath && ADMIN_TOURS_PAGE_PATH_TO_PAGE_SUB_PARAMS_MAPPING[tourStateSubPath]
    ? ADMIN_TOURS_PAGE_PATH_TO_PAGE_SUB_PARAMS_MAPPING[tourStateSubPath].screenId
    : ADMIN_TOURS_PAGE_PATH_TO_PAGE_PARAMS_MAPPING[tourStatePath]
    ? ADMIN_TOURS_PAGE_PATH_TO_PAGE_PARAMS_MAPPING[tourStatePath].screenId
    : SCREEN_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
)
@withScrollToTop
@branch({
  marketsById: ['markets', 'byId'],
  currentUser: ['currentUser'],
  partnersList: ['partners', 'list'],
})
class AdminToursPage extends Component {
  constructor(props) {
    super(props);

    const marketId =
      lodashGet('tourSupport.marketId', props.currentUser.settings) ||
      USER_SETTING_TOUR_SUPPORT_FIELD_MARKET_ID_ALL;

    this.state = {
      nightMode: false,
      sortBy: TOUR_ADMIN_SORT_BY_START_DATE,
      marketId,
    };
  }

  static fetchData(match, inject) {
    return Promise.all([
      AgentActions.getAgentsList(inject),
      HouseholdActions.getHouseholdAdminsList(inject),
      MarketActions.getList(inject),
      PartnerActions.getPartnerList(inject),
    ]);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Admin Tours | Sunroom Rentals',
    section: 'Admin',
  });

  static propTypes = {
    marketsById: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    partnersList: PropTypes.array,
  };

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(AdminActions.getItemsForToursIndicators);
  }

  _onNightModeClick = () => {
    this.setState((prevState) => ({
      nightMode: !prevState.nightMode,
    }));
  };

  _onSortByDropdownClick = (e) => {
    this.setState({
      sortBy: e.target.dataset.sortBy,
    });
  };

  _onMarketChange = (marketId) => {
    this.setState({ marketId });
  };

  render() {
    const {
      match: {
        params: { tourStatePath, tourStateSubPath, userId },
      },
      marketsById,
      currentUser,
      dispatch,
      partnersList,
    } = this.props;
    const pageParams = ADMIN_TOURS_PAGE_PATH_TO_PAGE_PARAMS_MAPPING[tourStatePath];
    const pageSubParams = ADMIN_TOURS_PAGE_PATH_TO_PAGE_SUB_PARAMS_MAPPING[tourStateSubPath];

    const { nightMode, sortBy, marketId } = this.state;

    if (!pageParams) {
      return <Redirect to={toursAdminLinks[0].value} />;
    }

    const { headline, dataTableViewId, sublinks, showSort, showFilters } = pageParams;
    const marketList = lodashValues(marketsById);
    const userSettings = currentUser.settings.tourSupport || {};

    const currentDataTableViewId =
      pageSubParams && pageSubParams.dataTableViewId
        ? pageSubParams.dataTableViewId
        : dataTableViewId;

    return (
      <div className="page-tourSupportDashboard">
        <div className={classnames({ nightMode: nightMode })}>
          <div className="admin-navbar">
            <div className="logo-container">
              <SrLogo redirectTo="/admin" />
            </div>
            <SrAdminNavbarLinks links={toursAdminLinks} currentLocationPath={tourStatePath} />
            <SrSearchByPhoneHouseholdIdAndRedirectForm
              getRedirectionPath={(user) => `/admin/tours/all/user/${user.id}`}
            />
          </div>
          <div className="admin-subnav">
            <div className="admin-subnav-left">
              <H1>{`${headline}${userId ? ' by user #' + userId : ''}`}</H1>
              {sublinks && (
                <SrAdminNavbarLinks
                  links={sublinks}
                  currentLocationPath={tourStateSubPath}
                  className="admin-subnav-sublinks-container"
                />
              )}
            </div>
            <div className="admin-subnav-right">
              {showSort && (
                <div className="sortContainer">
                  <span className="sortContainerLabel">
                    {`sort: ${
                      sortBy === TOUR_ADMIN_SORT_BY_RECENT_ACTIVITY ? 'recent' : 'touring soon'
                    }`}
                  </span>
                  <div className="dropdownContainer">
                    <span
                      data-sort-by={TOUR_ADMIN_SORT_BY_START_DATE}
                      onClick={this._onSortByDropdownClick}
                      className={classnames('option', {
                        active: sortBy === TOUR_ADMIN_SORT_BY_START_DATE,
                      })}
                    >
                      touring soon
                    </span>
                    <span
                      data-sort-by={TOUR_ADMIN_SORT_BY_RECENT_ACTIVITY}
                      onClick={this._onSortByDropdownClick}
                      className={classnames('option', {
                        active: sortBy === TOUR_ADMIN_SORT_BY_RECENT_ACTIVITY,
                      })}
                    >
                      recent
                    </span>
                  </div>
                </div>
              )}
              {showFilters && (
                <SrUserSettingDropdown
                  user={currentUser}
                  settingField="tourSupport.marketId"
                  settingValue={marketId}
                  optionTitle="market"
                  options={[
                    {
                      value: USER_SETTING_TOUR_SUPPORT_FIELD_MARKET_ID_ALL,
                      label: 'all',
                    },
                    ...marketList.map((market) => ({
                      value: market.id,
                      label: market.displayName,
                    })),
                  ]}
                  onChange={this._onMarketChange}
                  dispatch={dispatch}
                />
              )}
              {showFilters && (
                <SrUserSettingListingManagementCompanyDropdown
                  currentUser={currentUser}
                  settingField="tourSupport.listingManagementCompany"
                />
              )}
            </div>
          </div>
          <SrDataTableAdminTours
            viewId={currentDataTableViewId}
            userId={userId}
            sortBy={sortBy}
            marketId={marketId === USER_SETTING_TOUR_SUPPORT_FIELD_MARKET_ID_ALL ? null : marketId}
            listingManagementCompany={userSettings.listingManagementCompany}
            partnersList={partnersList || []}
          />
          <div className="nightModeToggle" onClick={this._onNightModeClick} />
        </div>
      </div>
    );
  }
}

AdminToursPage.displayName = 'AdminToursPage';

export default AdminToursPage;
